import {
  addDays,
  addYears,
  differenceInDays,
  differenceInYears,
  formatISO,
  lightFormat,
  parse,
  parseISO,
  startOfDay,
  startOfYear,
  subYears,
} from 'date-fns';

export const DateFormat = 'yyyy-MM-dd';

/**
 * Get a date object dating the next Year in the current
 * first day in the first month of next Year.
 * @param { Date } date Optional date
 * @returns { Date } Date object set to the next year with first month and first day
 */
export const getNextYear = (date = new Date()): Date =>
  startOfYear(addYears(date, 1));

/**
 * Get a date object dating the next day in the current
 * month or first day in the next month.
 * @param { Date } date Optional date to get next day from
 * @returns { Date } Date object set to the next day
 */
export const getNextDay = (date = new Date()): Date =>
  startOfDay(addDays(date, 1));

/**
 * Get the date N years before the given date or now.
 * @param years
 * @param { Date } date Optional date
 * @returns { Date } Date N year before
 */
export const getDayNYearsBefore = (years: number, date = new Date()): Date =>
  startOfDay(subYears(date, years));

/**
 * Get a date object dating the first day in the coming month
 * @param {Date} now reference date, optional
 * @return {String|Date} result
 */
export function getFirstOfNextMonth(now: Date = new Date()) {
  // Create local copy, so we keep passed value original
  const month = now.getMonth();
  const resultDate = new Date(now.getTime());

  // Set day to first
  resultDate.setDate(1);

  // Is last month in year
  if (now.getMonth() === 11) {
    resultDate.setMonth(0);
    resultDate.setFullYear(resultDate.getFullYear() + 1);
  } else {
    resultDate.setMonth(month + 1);
  }

  return startOfDay(resultDate);
}

/**
 * Get the date N years next the given date or now.
 * @param years
 * @param { Date } date Optional date
 * @returns { Date } Date N year next
 */
export const getNextNYears = (years: number, date = new Date()): Date =>
  addYears(date, years);

/**
 * Get the date 100 years before the given date or now.
 * @param { Date } date Optional date
 * @returns { Date } Date 100 year before
 */
export const getDay100YearsBefore = (date = new Date()): Date =>
  getDayNYearsBefore(100, date);

/**
 * Get locale date string from given date.
 * @param date { Date | String }
 * @param options { Intl.DateTimeFormatOptions }
 * @returns { String } Formatted date string
 */
export const getLocaleDateString = (
  date: Date | string,
  options?: Intl.DateTimeFormatOptions,
): string => {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  };

  const dateObject = typeof date === 'string' ? new Date(date) : date;

  return dateObject.toLocaleDateString('de-DE', {
    ...(options !== undefined ? options : defaultOptions),
  });
};

/**
 * Get date in ISO date format as string
 * @param date { Date }
 * @returns { String } ISO date string
 */
export const getIsoDateString = (date = new Date()): string =>
  formatISO(date, { representation: 'date' });

/**
 * Get date in filename format as string
 * @param date { Date }
 * @returns { String } string
 */
export const getFilenameDateString = (date = new Date()): string =>
  lightFormat(date, 'yyyy-MM-dd_hh-mm-ss');

/**
 * Adds days to a given date
 * @param date { Date | number }
 * @param amount { number}
 * @returns { Date } date
 */
export const addDaysToDate = (date: Date | number, amount: number): Date =>
  addDays(date, amount);

/**
 * Returns an array with the last n years as string
 * @param n { number }
 * @returns { Array } array of last n years as string
 */
export const getLastNYears = (n: number): string[] => {
  if (n <= 0 || typeof n !== 'number') {
    return [];
  }

  const now = new Date();
  const result = [];

  for (let i = 0; i < n; i += 1) {
    result.push(subYears(now, i).getFullYear().toString());
  }

  return result;
};

/**
 * Checks if a given value is a date. Also works as type guard
 * @param value
 * @returns boolean (TypeGuard is Date)
 */
export const isDate = (value: unknown): value is Date =>
  value instanceof Date ||
  (typeof value === 'object' &&
    Object.prototype.toString.call(value) === '[object Date]');

/**
 * Returns the difference between to dates in days or years.
 * Examples:
 * getDifference('2020-08-02', '2020-08-01') => 1
 * getDifference('2020-08-01', '2020-08-05') => -4
 * @param {Date | String} dateOne represents the first date on
 * which the difference will be calculated
 * @param {Date | String} dateTwo represents the date on whiche the difference to
 * dateOne is calculated
 * @param {boolean} inYears determine if you want to have the result in days or years
 * @returns {number} the difference.
 */
export function getDifference(
  dateOne: Date | string,
  dateTwo: Date | string,
  inYears = false,
): number {
  const dateA = dateOne instanceof Date ? dateOne : new Date(dateOne);
  const dateB = dateTwo instanceof Date ? dateTwo : new Date(dateTwo);
  if (inYears) {
    return differenceInYears(dateA, dateB);
  }

  return differenceInDays(dateA, dateB);
}

/**
 * Wrapper around date-fns parse function.
 * Helps to get consistent date objects with the same timezone / time settings
 *
 * @param { String } dateString
 * @returns { Date }
 */
export const parseDate = (dateString: string): Date =>
  parse(dateString, 'yyyy-MM-dd', new Date());

/**
 * Wrapper around date-fns parseISO function.
 *
 * @param { String } dateString
 * @returns { Date }
 */
export const parseISODate = (dateString: string): Date => parseISO(dateString);

/**
 * Wrapper to get the long version of month from given date
 *
 * @param date
 * @returns { string }
 */
export const getMonthName = (date: Date): string =>
  date.toLocaleDateString('default', { month: 'long' });

export const getCurrentYear = (): number => new Date().getFullYear();

export const calculateAge = (birthDate: Date, date: Date | null = null) =>
  differenceInYears(date === null ? Date.now() : date, birthDate);
